import { useState, useRef, useEffect } from 'react';
import { Link } from "react-router-dom";
import { FinalCTASection } from "@/components/redesigned/hero/sections/FinalCTA";

const Footer = ({ cta = true }: { cta?: boolean }) => {
  const brandTextRef = useRef<HTMLDivElement>(null);
  const [brandTextStyle, setBrandTextStyle] = useState<React.CSSProperties>({
    transform: 'translateY(50vh)',
    opacity: 0,
  });

  useEffect(() => {
    const handleScroll = () => {
      if (!brandTextRef.current) return;

      const rect = brandTextRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementTop = rect.top;

      const transitionStart = viewportHeight * 1.5;
      const transitionEnd = viewportHeight * 0.5;

      let progress = 0;
      if (elementTop < transitionStart) {
        progress = Math.min(1, Math.max(0,
          (transitionStart - elementTop) / (transitionStart - transitionEnd)
        ));
      }

      const translateY = 20 - (progress * 22);
      const opacity = Math.min(1, progress * 1.5);

      setBrandTextStyle({
        transform: `translateY(${translateY}vh)`,
        opacity: opacity,
      });
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  return (
    <>
    {cta && <FinalCTASection />}
    <footer className="bg-background text-card-foreground relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        {/* Footer Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          {/* Resources */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Resources</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/blog" className="hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/roadmap" className="hover:text-primary transition-colors">Roadmap</Link></li>
              <li><Link to="/changelog" className="hover:text-primary transition-colors">Changelog</Link></li>
              <li><Link to="/help-center" className="hover:text-primary transition-colors">Help Center</Link></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Support</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/contact" className="hover:text-primary transition-colors">Contact Support</Link></li>
              <li><Link to="/feature-request" className="hover:text-primary transition-colors">Feature Request</Link></li>
              <li><Link to="/bug-report" className="hover:text-primary transition-colors">Bug Report</Link></li>
              <li><Link to="/community" className="hover:text-primary transition-colors">Community Forum</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><Link to="/privacy" className="hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-primary transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/cookies" className="hover:text-primary transition-colors">Cookie Policy</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Contact</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>+421 902 691 939</li>
              <li><a href="mailto:info@taginn.app" className="hover:text-primary transition-colors">info@taginn.app</a></li>
              <li>Based in EU - Slovakia</li>
              <li className="pt-2 text-xs">
                IČO: 55959652<br />
                DIČ: 1128572753
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© 2026 TAG-Innovation. All rights reserved.</p>
          <div className="flex items-center gap-4">
            {/* Social links could go here */}
          </div>
        </div>
      </div>

      {/* Big Brand Text */}
      <div className="relative w-full -mt-12 z-20">
        <div
          ref={brandTextRef}
          className="w-full text-center pointer-events-none"
          style={brandTextStyle}
        >
          <a
            href="https://taginn.sk"
            target="_blank"
            rel="noopener noreferrer"
            className="pointer-events-auto inline-block text-center hover:opacity-80 transition-opacity"
          >
            <p className="text-sm text-muted-foreground mb-1">Developed by</p>
            <h2
              className="font-bold text-primary whitespace-nowrap leading-none"
              style={{
                fontSize: 'min(12vw, calc(100vw / 8))',
                maxWidth: '100vw',
                margin: '0 auto'
              }}
            >
              TAG-Innovation
            </h2>
          </a>
        </div>
      </div>
    </footer>
    </>
  );
};

export default Footer;
